import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import cx from 'classnames'
import { type ReactNode, Suspense } from 'react'

import { buttonStyles } from '../../actions/button/buttonStyles'
import { CircularSpinner } from '../../feedback/circularSpinner/CircularSpinner'
import { Icon } from '../../structure/icon/Icon'
import { useDisclosureSectionContext } from './context/disclosureSectionContext'
import { DisclosureSectionProvider } from './context/DisclosureSectionProvider'

type Props = {
  className?: string
  children: ReactNode
}

/**
 * Used for showing a section with a title and paragrahs
 */
export const DisclosureSection = ({
  children,
  isOpenByDefault,
  className,
}: Props & {
  isOpenByDefault?: boolean
}) => (
  <Disclosure as="div" defaultOpen={isOpenByDefault} className={className}>
    {({ open }) => (
      <DisclosureSectionProvider isOpen={open}>
        {children}
      </DisclosureSectionProvider>
    )}
  </Disclosure>
)

/**
 * Default styling wrapped that has been added to try and keep the same layout
 */
const Wrapper = ({ children, className }: Props) => (
  <div className={cx('w-full', className)}>
    <div className="mx-auto grid w-full gap-2">{children}</div>
  </div>
)
DisclosureSection.Wrapper = Wrapper

/**
 * Title of the section
 */
const Title = ({
  children,
  className,
  isDanger = false,
  variant = 'secondary',
}: Props & { isDanger?: boolean; variant?: 'primary' | 'secondary' }) => {
  const { isOpen } = useDisclosureSectionContext()

  return (
    <DisclosureButton
      className={cx(
        buttonStyles({
          variant,
          disabled: false,
          isFullWidth: false,
          isDanger,
        }),
        className,
      )}
    >
      {children}
      <Icon
        name="ChevronUp"
        className={cx('h-5 w-5 transition-transform', {
          'rotate-180 transform': isOpen,
        })}
      />
    </DisclosureButton>
  )
}
DisclosureSection.Title = Title

type BodyProps = Props & {
  shouldWrapWithSuspense?: boolean
}

/**
 * Body of the section
 */
const Body = ({
  children,
  className,
  shouldWrapWithSuspense = true,
}: BodyProps) => (
  <DisclosurePanel className={cx('px-1 overflow-auto', className)}>
    {shouldWrapWithSuspense ? (
      <Suspense fallback={<CircularSpinner />}>{children}</Suspense>
    ) : (
      children
    )}
  </DisclosurePanel>
)
DisclosureSection.Body = Body
